import { Link } from 'react-router-dom';
import {
  benefitDetailUrl,
  tryGetBenefitServiceSeqNo,
} from '#/domains/landing/utils/benefitDetailUrl';
import { ctaLinkProps } from '#/domains/landing/utils/ctaLink';

function sanitizeHref(rawHref: string): string {
  if (rawHref.startsWith('/')) return rawHref;
  if (/^https?:\/\//i.test(rawHref)) return rawHref;
  return '#';
}

function getBenefitInlineLinkId(serviceSeqNo: string): string {
  return `benefit-inline-link-${serviceSeqNo}`;
}

/**
 * Renders `[label](href)` as real links and `**text**` as bold spans.
 * Plain text and punctuation (e.g. quotes around a link) stay as text.
 */
export default function InlineLinks({ text }: { text: string }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*\n]+\*\*)/);
  return (
    <>
      {parts.map((part, i) => {
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          const href = sanitizeHref(linkMatch[2].trim());
          const benefitServiceSeqNo = tryGetBenefitServiceSeqNo(href);
          // Benefit detail links open in a new browsing context so the article
          // WebView is preserved underneath in-app — system back returns here.
          if (benefitServiceSeqNo) {
            return (
              <a
                key={i}
                href={ctaLinkProps(benefitDetailUrl(benefitServiceSeqNo)).href}
                id={getBenefitInlineLinkId(benefitServiceSeqNo)}
                className="font-medium text-blue-55 underline underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                {linkMatch[1]}
              </a>
            );
          }
          const isExternal = /^https?:\/\//i.test(href);
          if (!isExternal) {
            return (
              <Link
                key={i}
                to={href}
                className="font-medium text-blue-55 underline underline-offset-2"
              >
                {linkMatch[1]}
              </Link>
            );
          }
          return (
            <a
              key={i}
              href={ctaLinkProps(href).href}
              className="font-medium text-blue-55 underline underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkMatch[1]}
            </a>
          );
        }
        const boldMatch = part.match(/^\*\*([^*\n]+)\*\*$/);
        if (boldMatch) {
          return (
            <strong key={i} className="font-bold text-gray-100">
              {boldMatch[1]}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
